import React, {useEffect, useRef, useState} from 'react';

import {loadingBarStyles} from 'js-libs/sweetcommons/styles';

const UPDATE_TIME = 200;
const MAX_PROGRESS = 99;
const PROGRESS_INCREASE = 10;
const ANIMATION_TIME = UPDATE_TIME * 4;
const TERMINATING_ANIMATION_TIME = UPDATE_TIME / 2;

export interface SweetLoadingBarCommonProps {
  animationType?: 'width' | 'transform';
  className?: string;
  maxProgress?: number;
  progressIncrease?: number;
  updateTime?: number;
  style?: Record<string, string | number>;
}
interface Props extends SweetLoadingBarCommonProps {
  loading: number;
}

export function SweetLoadingBar({
  animationType = 'width',
  loading,
  className,
  maxProgress = MAX_PROGRESS,
  progressIncrease = PROGRESS_INCREASE,
  updateTime = UPDATE_TIME,
  style = loadingBarStyles,
}: Props): JSX.Element {
  const [percent, _setPercent] = useState<number>(0);
  const percentRef = useRef(percent);

  const setPercent = (percent: number) => {
    percentRef.current = percent;
    _setPercent(percent);
  };

  const endAnimationRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null);

  function clearProgressInterval() {
    if (progressIntervalRef.current != null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }

  function clearTerminatingAnimationTimeout() {
    if (endAnimationRef.current != null) {
      clearTimeout(endAnimationRef.current);
      endAnimationRef.current = null;
    }
  }

  useEffect(() => {
    return () => {
      clearProgressInterval();
      clearTerminatingAnimationTimeout();
    };
  }, []);

  useEffect(() => {
    if (loading > 0) {
      launch();
    } else {
      if (percent === 0) {
        resetProgress();
      } else {
        setPercent(100);
      }
    }
  }, [loading > 0]);

  function resetProgress() {
    setPercent(0);
    clearProgressInterval();
    clearTerminatingAnimationTimeout();
  }

  const shouldShow = () => percent > 0 && percent <= 100;

  const calcNewPercent = (percent: number, progressIncrease: number) => {
    // Use cos as a smoothing function
    // Can be any function to slow down progress near the 100%
    const smoothedProgressIncrease =
      progressIncrease * Math.cos(percent * (Math.PI / 2 / 100));
    return percent + smoothedProgressIncrease;
  };

  const simulateProgress = () => {
    const percent = percentRef.current;
    const newPercent = calcNewPercent(percent, progressIncrease);

    if (percent === 100 && progressIntervalRef.current != null) {
      clearProgressInterval();
      endAnimationRef.current = window.setTimeout(
        resetProgress,
        TERMINATING_ANIMATION_TIME
      );
    } else if (newPercent < maxProgress) {
      setPercent(newPercent);
    }
  };

  const buildStyle = () => {
    const animationTime =
      percent !== 100 ? ANIMATION_TIME : TERMINATING_ANIMATION_TIME;

    let mergedStyle: any =
      animationType === 'width'
        ? getWidthAnimationStyle(animationTime)
        : getTransformAnimationStyle(animationTime);

    // Use default styling if there's no CSS class applied
    if (!className) {
      mergedStyle.height = '3px';
      mergedStyle.position = 'absolute';
    }

    return {...mergedStyle, opacity: shouldShow() ? 1 : 0, ...style};
  };

  const getWidthAnimationStyle = (animationDuration: number) => ({
    width: `${percent}%`,
    transition: `width ${animationDuration}ms linear`,
    willChange: 'width, opacity',
  });

  const getTransformAnimationStyle = (animationDuration: number) => ({
    transform: `translate3d(${percent - 100}%, 0px, 0px)`,
    msTransform: `translate3d(${percent - 100}%, 0px, 0px)`,
    WebkitTransform: `translate3d(${percent - 100}%, 0px, 0px)`,
    MozTransform: `translate3d(${percent - 100}%, 0px, 0px)`,
    OTransform: `translate3d(${percent - 100}%, 0px, 0px)`,
    transition: `transform ${animationDuration}ms linear 0s`,
    msTransition: `-ms-transform ${animationDuration}ms linear 0s`,
    WebkitTransition: `-webkit-transform ${animationDuration}ms linear 0s`,
    MozTransition: `-moz-transform ${animationDuration}ms linear 0s`,
    OTransition: `-o-transform ${animationDuration}ms linear 0s`,
    width: '100%',
    willChange: 'transform, opacity',
  });

  function launch() {
    if (!progressIntervalRef.current) {
      progressIntervalRef.current = window.setInterval(
        simulateProgress,
        updateTime
      );
    }

    if (percent === 100 && endAnimationRef.current != null) {
      clearTimeout(endAnimationRef.current);
    }

    setPercent(0);
  }

  return (
    <div>
      <div style={buildStyle()} className={className} />
    </div>
  );
}
